import { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classes from './StudioServices.module.css';
import { getLowestPackagePrice } from '../../util/getLowestPackagePrice';
import { useBooking } from '../../store/bookingStore';
import { NavBarContext } from '../../App';
import { useDropdown } from '../../hooks/useDropdown';
import MusicProjectModal from '../../components/CreativeEditing/MusicProjectModal';
import {
    buildBookingAuthNavigationState,
    buildStudioBookingRedirectLocation,
    storeStudioBookingRedirect,
} from '../../util/bookingAuthRedirect';

const PlayIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 28 34" fill="none">
      <path d="M26.6267 14.3973C28.3172 15.4366 28.3172 18.035 26.6267 19.0744L3.80507 33.1059C2.11458 34.1452 0.00147438 32.846 0.00147438 30.7673L0.00147438 2.70438C0.00147438 0.625646 2.11458 -0.67356 3.80507 0.365807L26.6267 14.3973Z" fill="white"/>
    </svg>
);

export const StudioServices = ({ 
                                selectedOpenCreativeModal, 
                                setSelectedOpenCreativeModal, 
                                fromBooking = false, 
                                processedProfilePicture, 
                                firstName, 
                                userId, 
                                studioId, 
                                studioCreativesData, 
                                hasSelectedPackages = false,
                                setShowBookingProcedure,
                                setShowBookingPhone,
                                onBookingCarouselIndexChange,
                            }) => {

    const { bookingData, updateBookingData } = useBooking();
    const { isOpen: showDropdown, toggle: toggleDropdown, triggerRef, dropdownRef } = useDropdown();
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [currentCreativeIndex, setCurrentCreativeIndex] = useState(0);
    const { isPhoneSize, setShowSignIn } = useContext(NavBarContext);
    const navigate = useNavigate();
    const location = useLocation();
    const touchStartXRef = useRef(null);
    const [selectedMusicProject, setSelectedMusicProject] = useState(null);
    const [isMusicModalOpen, setIsMusicModalOpen] = useState(false);
    
    // Get current signed-in user
    const { user: currentUser, isAuthenticated } = useSelector((state) => state?.auth || {});
    const currentUserId = currentUser?.id || currentUser?._id;
    
    // Check if the current user owns this studio
    const isStudioOwner = currentUserId && userId && currentUserId === userId;
    
    const [addFreelancer, setAddFreelancer] = useState(bookingData?.addFreelancer || false);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sync selectedOpenCreativeModal with addFreelancer when in booking flow
    // Only auto-open if addFreelancer is true AND no packages are already selected
    // This prevents the modal from opening when user clicks "Mitbuchen" and package is already added
    useEffect(() => {
        const hasPackages = bookingData?.creativePackages && bookingData.creativePackages.length > 0;
        if (fromBooking && bookingData?.addFreelancer && !selectedOpenCreativeModal && !hasPackages) {
            setSelectedOpenCreativeModal(true);
        }
    }, [bookingData?.addFreelancer, bookingData?.creativePackages, fromBooking]);

    const lowestPackagePrice = useMemo(() => getLowestPackagePrice(studioCreativesData), [studioCreativesData]);

    const totalCreatives = studioCreativesData?.length || 0;
    const safeIndex = totalCreatives > 0 ? Math.min(currentCreativeIndex, totalCreatives - 1) : 0;
    const primaryLink = studioCreativesData?.[safeIndex] || null;
    const creative = primaryLink?.creative || null;

    useEffect(() => {
        if (fromBooking && onBookingCarouselIndexChange) {
            onBookingCarouselIndexChange(safeIndex);
        }
    }, [fromBooking, safeIndex, onBookingCarouselIndexChange]);

    // Determine if switch should be checked and disabled
    // When disabled (packages exist), it should always appear checked (visual indicator only)
    // When enabled, it reflects the actual modal state
    const isSwitchChecked = hasSelectedPackages ? true : selectedOpenCreativeModal;
    const isSwitchDisabled = hasSelectedPackages;

    const servicesArray = useMemo(() => {
        if (creative?.skills && Array.isArray(creative.skills) && creative.skills.length > 0) {
            return creative.skills;
        }
    }, [creative]);

    const isMusicCreative = creative?.creativeType === 'MUSIC';

    const musicProjects = useMemo(
        () => (creative?.projects?.items && Array.isArray(creative.projects.items) ? creative.projects.items : []),
        [creative]
    );

    const visualMediaItems = useMemo(() => {
        if (!creative?.allProcessedProjectImages?.length) return [];

        return creative.allProcessedProjectImages.slice(0, 5)
            .map((item) => {
                const isLink =
                    item.type === 'link' ||
                    (item.url && typeof item.url === 'string' &&
                        (item.url.includes('youtube.com') ||
                            item.url.includes('youtu.be') ||
                            item.url.includes('vimeo.com')));

                const isVideoFile =
                    item.type === 'video' ||
                    (item.url && typeof item.url === 'string' &&
                        /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(item.url));

                let displayUrl = '';

                if (isLink) {
                    // OLD behavior for YouTube/Vimeo → use raw thumbnailUrl
                    displayUrl = item.thumbnailUrl || '';
                } else if (isVideoFile && (item.processedThumbnailUrl || item.thumbnailUrl)) {
                    // Uploaded video with thumbnail → prefer processed thumbnail, then raw thumbnail
                    displayUrl = item.processedThumbnailUrl || item.thumbnailUrl || '';
                } else {
                    // Pure images
                    displayUrl = item.processedUrl || item.url || '';
                }

                return {
                    ...item,
                    displayUrl,
                    isLink,
                    isVideoFile,
                    isPhoto: !isLink && !isVideoFile,
                };
            })
            // Only keep items that actually have something to show
            .filter((item) => !!item.displayUrl);
    }, [creative?.allProcessedProjectImages]);

    const handleMusicProjectClick = (project) => {
        if (!project) return;
        setSelectedMusicProject(project);
        setIsMusicModalOpen(true);
    };

    const handleCloseMusicModal = () => {
        setIsMusicModalOpen(false);
        setSelectedMusicProject(null);
    };

    const getServicesToShow = () => {
        if (screenWidth >= 1199) {
            return 4; // Show 4
        } else if (screenWidth >= 1039 && screenWidth <= 1198) {
            return 3; // Show 3
        } else if (screenWidth >= 742 && screenWidth <= 1039) {
            return 2;
        } else if (screenWidth >= 696 && screenWidth <= 741) {
            return 4; // Show 4 (based on your CSS change)
        } else if (screenWidth >= 536 && screenWidth <= 695) {
            return 3; // Show 2 (based on your CSS change)
        } else if (screenWidth >= 400 && screenWidth <= 535) {
            return 2; // Show 2 (based on your CSS change)
        } else {
            return 2; // Show 2 (based on your CSS change)
        }
    };

    const getHiddenCount = () => {
        const servicesToShow = getServicesToShow();
        const hiddenCount = creative?.skills?.length - servicesToShow;
        return hiddenCount > 0 ? hiddenCount : 0;
    };

    const toggleOpenCreativeModal = () => {
        // Don't allow toggling if switch is disabled (packages exist in booking)
        if (isSwitchDisabled) return;
        const newValue = !selectedOpenCreativeModal;
        setSelectedOpenCreativeModal(newValue);
        // Sync addFreelancer state when toggling in booking flow
        if (fromBooking) {
            updateBookingData('addFreelancer', newValue);
        }
    };

    const handleNextCreative = () => {
        if (!totalCreatives || totalCreatives <= 1) return;
        setCurrentCreativeIndex((prev) => (prev + 1) % totalCreatives);
    };

    const handlePrevCreative = () => {
        if (!totalCreatives || totalCreatives <= 1) return;
        setCurrentCreativeIndex((prev) => (prev - 1 + totalCreatives) % totalCreatives);
    };

    const handleTouchStart = (e) => {
        if (!e.touches || e.touches.length === 0) return;
        touchStartXRef.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (touchStartXRef.current === null) return;
        const touchEndX = e.changedTouches?.[0]?.clientX;
        if (typeof touchEndX !== 'number') {
            touchStartXRef.current = null;
            return;
        }
        const deltaX = touchEndX - touchStartXRef.current;
        const swipeThreshold = 50;
        if (deltaX > swipeThreshold) {
            handlePrevCreative();
        } else if (deltaX < -swipeThreshold) {
            handleNextCreative();
        }
        touchStartXRef.current = null;
    };

    const handleAddFreelancerClick = () => {
        // Don't allow booking if user owns the studio
        if (isStudioOwner) {
            return;
        }
        
        // Set addFreelancer to true
        const newAddFreelancerValue = true;
        setAddFreelancer(newAddFreelancerValue);
        updateBookingData('addFreelancer', newAddFreelancerValue);

        const bookingTarget = buildStudioBookingRedirectLocation({
            studioId,
            isPhoneSize,
            currentLocation: location,
        });

        if (!isAuthenticated && !currentUserId) {
            storeStudioBookingRedirect({
                studioId,
                isPhoneSize,
                currentLocation: location,
            });
            const state = buildBookingAuthNavigationState({ location, from: bookingTarget });

            if (isPhoneSize) {
                navigate('/anmelden', { state });
            } else if (setShowSignIn) {
                setShowSignIn(true);
            } else {
                navigate('/', { state });
            }
            return;
        }
        
        // Open booking procedure modal - use phone or desktop based on device size
        if (isPhoneSize && setShowBookingPhone) {
            setShowBookingPhone();
        } else if (!isPhoneSize && setShowBookingProcedure) {
            setShowBookingProcedure(true);
        }
    };

    return (
        <>
        <div className={classes.container} style={{ paddingBottom: fromBooking ? '40px' : '0px' }}>
            {!fromBooking && (
                <h3 className='titleSemibold3'>Zusätzlicher Studio-Service</h3>
            )}
            <div 
                className={classes.carouselWrapper}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <div className={classes.mainContainer}>
                <header className={classes.containerHeader}>
                    <div className={classes.userContainer}>
                        <div className={classes.userImageContainer}>
                            <img src={creative?.processedProfilePicture || processedProfilePicture} alt="User" />
                        </div>
                        <div className={classes.userInfos}>
                            <h3 className='titleSemibold3'>{creative?.displayName || firstName}</h3>
                            <span className='regularMiddle'>
                                {creative?.jobTitle || 'Host/Fotografin'}
                            </span>
                        </div>
                    </div>

                    {!fromBooking && (
                    <div className={classes.headerPrice}>
                        <span>
                            {lowestPackagePrice !== null && lowestPackagePrice !== undefined 
                                ? `${lowestPackagePrice}€ pro Stunde`
                                : 'Preis auf Anfrage'
                            }
                        </span>
                    </div>
                    )}

                </header>

                {fromBooking && (
                    <div className={classes.headerPrice}>
                        <span>
                            {lowestPackagePrice !== null && lowestPackagePrice !== undefined 
                                ? `${lowestPackagePrice}€ pro Stunde`
                                : 'Preis auf Anfrage'
                            }
                        </span>
                    </div>
                )}
                
                {fromBooking && (
                    <div className={classes.horizontalLineBooking}></div>
                )}
                
                {!fromBooking && (
                    <div className={classes.horizontalLine}></div>
                )}

                <div className={classes.servicesContainer}>
                    {creative?.skills?.slice(0, getServicesToShow()).map((service, index) => (
                        <div className={classes.serviceItem} key={index}>
                            <span className='regularMiddle'>{service}</span>
                        </div>
                    ))}
                    {creative?.skills?.length > getServicesToShow() && (
                        <button 
                            ref={triggerRef}
                            className={`${classes.allViewButton} mediumLarge`}
                            onClick={toggleDropdown}
                        >
                            +{creative?.skills?.length - getServicesToShow()}
                        </button>
                    )}
                    {showDropdown && (
                        <div ref={dropdownRef} className={classes.dropdownContainer}>
                            {(() => {
                                const servicesToShow = getServicesToShow();
                                const hiddenServices = servicesArray.slice(servicesToShow, servicesArray.length);                                
                                return hiddenServices.map((service, index) => (
                                    <div className={`${classes.serviceItem} ${classes.dropdownServiceItem}`} key={index + servicesToShow}>
                                        <span className='regularMiddle'>{service}</span>
                                    </div>
                                ));
                            })()}
                        </div>
                    )}
                </div>
                <div className={classes.jobsContainer}>
                    <h2 className='titleSemibold3'>Arbeiten</h2>
                    <div className={classes.jobsContent}>
                        {isMusicCreative && musicProjects.length > 0 ? (
                            musicProjects.slice(0, 4).map((project) => {
                                const audioData = project.audios?.[0];
                                const coverImage =
                                    audioData?.processedCoverImage ||
                                    audioData?.coverImage ||
                                    project.images?.[0]?.url ||
                                    project.processedImages?.[0]?.processedUrl;

                                return (
                                    <button
                                        type="button"
                                        className={`${classes.jobItem} ${classes.musicJobItem}`}
                                        key={project.id}
                                        onClick={() => handleMusicProjectClick(project)}
                                    >
                                        {coverImage && <img src={coverImage} alt={project.title || 'Musik Projekt'} />}
                                        <div className={classes.musicPlayOverlay}>
                                            <div className={classes.musicPlayButtonCircle}>
                                                <PlayIcon />
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        ) : (
                            visualMediaItems.slice(0, 4).map((item, index) => (
                                <div className={classes.jobItem} key={item.displayUrl || index}>
                                    {item.isLink && (
                                        <div className={classes.jobLinkBadge}>
                                            <span>🔗</span>
                                        </div>
                                    )}
                                    {item.isLink ? (
                                        <img
                                            src={item.displayUrl}
                                            alt="Video-Vorschaubild"
                                        />
                                    ) : item.isVideoFile ? (
                                        <img
                                            src={item.displayUrl}
                                            alt="Video-Vorschaubild"
                                        />
                                    ) : item.displayUrl ? (
                                        <img
                                            src={item.displayUrl}
                                            alt="Job"
                                        />
                                    ) : null}
                                </div>
                            ))
                        )}
                    </div>
                </div>
                
                {fromBooking && (
                    <div className={classes.horizontalLineBooking}></div>
                )}
                
                {!fromBooking ? (
                <div className={classes.buttonsContainer}>
                    <Link
                        className={classes.buttonOpenProfileLink}
                        to={creative?.id ? `/members/${creative.id}` : `/members/${userId}`}
                        state={{ fromStudioId: studioId }}
                    >
                        <button className={`${classes.button} ${classes.buttonOpenProfile} mediumLarge`}>Profil öffnen</button>
                    </Link>
                    <button 
                        onClick={handleAddFreelancerClick} 
                        className={`${classes.button} ${classes.buttonBook} mediumLarge`}
                        disabled={isStudioOwner}
                        style={{ 
                            opacity: isStudioOwner ? 0.5 : 1, 
                            cursor: isStudioOwner ? 'not-allowed' : 'pointer' 
                        }}
                    >
                        Mitbuchen
                    </button>
                </div>
                ) : (
                <div className={classes.openPacketModal}>
                    <span>Fotograf/in dazubuchen</span>
                    <div className={classes.switchContainer}>
                        <label className={classes.switch}>
                            <input 
                                type="checkbox" 
                                checked={isSwitchChecked} 
                                onChange={toggleOpenCreativeModal}
                                disabled={isSwitchDisabled}
                            />
                            <span className={classes.slider}></span>
                        </label>
                    </div>
                </div>
                )}
            </div>

            {totalCreatives > 1 && (
                <div className={classes.carouselControls}>
                    <button 
                        type="button"
                        className={`${classes.carouselArrow} ${classes.carouselArrowLeft}`}
                        onClick={handlePrevCreative}
                        aria-label="Vorheriger Creative"
                    >
                        ‹
                    </button>
                    <div className={classes.carouselIndicator}>
                        <span className='regularMiddle'>
                            {safeIndex + 1} / {totalCreatives}
                        </span>
                    </div>
                    <button 
                        type="button"
                        className={`${classes.carouselArrow} ${classes.carouselArrowRight}`}
                        onClick={handleNextCreative}
                        aria-label="Nächster Creative"
                    >
                        ›
                    </button>
                </div>
            )}
            
            </div>
        </div>
        {isMusicCreative && musicProjects.length > 0 && (
            <MusicProjectModal
                open={isMusicModalOpen}
                onClose={handleCloseMusicModal}
                project={selectedMusicProject}
                creative={creative}
                projectsArray={musicProjects}
                fromMemberPage={true}
            />
        )}
        </>
    );
};

export default StudioServices;
